import {Injectable} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {Paginator} from '../../tools/pagination/paginator';
import {TableSorter} from '../../tools/table-sorter/table-sorter';
import {LogFilter} from '../../types/log-filter';
import {UserActivityService} from '../user-activity/user-activity.service';

@Injectable({
  providedIn: 'root'
})
export class QueryFilterService {

  private queryParams: any = {};
  private paginator: Paginator | null = null;
  private tableSorter: TableSorter | null = null;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private userActivity: UserActivityService,
  ) {
  }

  init(paginator: Paginator | null = null, tableSorter: TableSorter | null = null): void {
    this.queryParams = {...this.route.snapshot.queryParams};

    this.paginator = paginator;
    this.tableSorter = tableSorter;

    if (this.paginator) {
      const count = this.getNumberFilter('count');
      if (count !== null && count > 0) {
        this.paginator.countByPage = count;
      }

      const page = this.getNumberFilter('page');
      if (page !== null && page > 0) {
        // pageCount is only known once the first response comes back, raise it
        // beforehand so the paginator does not clamp the restored page down to 1
        this.paginator.pageCount = Math.max(this.paginator.pageCount, page);
        this.paginator.currentPage = page;
      }
    }

    if (this.tableSorter) {
      const sort = this.getFilter('sort');
      if (sort) {
        this.tableSorter.sortName = sort;
      }

      const order = this.getFilter('order');
      if (order === 'asc' || order === 'desc') {
        this.tableSorter.sortOrder = order;
      }
    }
  }

  setFilters(params: any): void {
    this.userActivity.reportFilters(params);
    this.queryParams = {...params};

    delete this.queryParams['groups'];

    if (this.paginator) {
      this.queryParams.page = this.paginator.currentPage;
      this.queryParams.count = this.paginator.countByPage;
    }

    if (this.tableSorter) {
      this.queryParams.sort = this.tableSorter.sortName;
      this.queryParams.order = this.tableSorter.sortOrder;
    }

    this.updateURL();
  }

  getFilter(name: string): any {
    return this.queryParams[name] ?? undefined;
  }

  /**
   * Query params are always strings, ids have to be cast back to numbers to
   * match the [ngValue]/[value] of the select options they are bound to.
   */
  getNumberFilter(name: string): number | null {
    const value = this.getFilter(name);

    if (value === undefined || value === null || value === '') {
      return null;
    }

    const number = Number(value);

    return Number.isFinite(number) ? number : null;
  }

  getNumberListFilter(name: string): number[] {
    const value = this.getFilter(name);

    if (!value) {
      return [];
    }

    return String(value).split(',')
      .map((id: string) => Number(id))
      .filter((id: number) => Number.isFinite(id));
  }

  /**
   * Booleans are serialized as 1/0 in the URL, '0' is a truthy string.
   */
  getBooleanFilter(name: string): boolean | null {
    const value = this.getFilter(name);

    if (value === undefined || value === null || value === '') {
      return null;
    }

    return value === '1' || value === 'true' || value === true;
  }

  initFilters(): LogFilter {
    return {
      id: this.getFilter('id') ?? '',
      date: {
        from: this.getFilter('startDate'),
        to: this.getFilter('endDate'),
      },
      productId: this.getNumberFilter('productId'),
      offerId: this.getNumberFilter('offerId'),
      accountType: this.getFilter('accountType') ?? null,
      partnerId: this.getNumberFilter('partnerId'),
      lang: this.getFilter('lang') ?? null,
      country: this.getFilter('country') ?? null,
      subid: this.getFilter('subid') ?? '',
      tc1: this.getFilter('tc1') ?? '',
      tc2: this.getFilter('tc2') ?? '',
      tc3: this.getFilter('tc3') ?? '',
      tc4: this.getFilter('tc4') ?? '',
      landingPageId: this.getNumberFilter('landingPageId'),
      landingPageActiveSince: this.getFilter('landingPageActiveSince') ?? null,
    };
  }

  private updateURL(): void {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: this.queryParams,
    }).then();
  }
}
